<template>
  <v-card>
    <form-header
      :form="form"
      :title="$lang('clients.title_create')"
      tour="client-form"
    >
      <template v-slot:left-buttons>
        <v-btn icon dark @click="cancel()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
      <template v-slot:toolbar-items>
        <v-btn dark text @click="submit()" :loading="loading">{{ $lang("main.crud.create") }}</v-btn>
      </template>
    </form-header>
    <v-card-text>
      <v-container v-if="!initialized">
        <v-row justify="center">
          <div class="text-center">
            <v-progress-circular :size="70" :width="7" color="primary" indeterminate></v-progress-circular>
          </div>
        </v-row>
      </v-container>
      <v-container v-if="initialized">
        <v-row>
          <v-col cols="12" xs="12" md="8">
            <v-row>
              <v-col cols="12" xs="12" md="6">
                <v-radio-group row v-model="form.type" :mandatory="false">
                  <v-radio
                    v-for="(type, i) in $in('ConstClientType')"
                    :key="i"
                    :label="type.text"
                    :value="type.value"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12" xs="12" md="6">
                <c-companies-autocomplete
                  v-model="form.company_id"
                  :label="$lang('clients.fields.company_assignation')"
                  :error="!!validation.company_id"
                  :error-messages="!!validation.company_id ? validation.company_id : []"
                  autoselect
                />
              </v-col>
            </v-row>
            <v-text-field
              v-model="form.name"
              prepend-icon="mdi-account"
              :error="!!validation.name"
              :label="form.type === $const('ConstClientType', 'PRIVATE') ? $lang('clients.fields.name') : $lang('clients.fields.company_name')"
              @blur="splitName(form.name)"
              :error-messages="!!validation.name ? validation.name : []"
            ></v-text-field>

            <div v-if="form.type === $const('ConstClientType', 'PRIVATE')" class="d-flex flex-wrap">
              <v-text-field
                class="d-order-1 mr-1"
                v-model="form.first_name"
                :label="$lang('clients.fields.first_name')"
              ></v-text-field>
              <v-text-field
                class="d-order-2 ml-1"
                v-model="form.last_name"
                :label="$lang('clients.fields.last_name')"
              ></v-text-field>
            </div>
            <v-text-field
              v-model="form.email"
              prepend-icon="mdi-email"
              :label="$lang('clients.fields.email')"
              :error="!!validation.email"
              :error-messages="!!validation.email ? validation.email : []"
            ></v-text-field>
            <v-text-field
              v-model="form.pec"
              prepend-icon="mdi-email-lock"
              :label="$lang('clients.fields.pec')"
              :error="!!validation.pec"
              :error-messages="!!validation.pec ? validation.pec : []"
            ></v-text-field>
            <v-text-field
              v-model="form.phone"
              prepend-icon="mdi-phone"
              :label="$lang('clients.fields.phone')"
              :error="!!validation.phone"
              :error-messages="!!validation.phone ? validation.phone : []"
            ></v-text-field>
            <v-text-field
              v-model="form.tax_code"
              :label="$lang('clients.fields.taxcode')"
            ></v-text-field>
            <v-text-field
              v-if="isCompany"
              v-model="form.vat_code"
              :label="$lang('clients.fields.vatcode')"
            ></v-text-field>
            <v-text-field
              v-model="form.sdi"
              :label="$lang('clients.fields.sdi')"
              :error="!!validation.sdi"
              :error-messages="!!validation.sdi ? validation.sdi : []"
            ></v-text-field>
            <v-expansion-panels flat>
              <v-expansion-panel>
                <v-expansion-panel-header class="text-h6">{{ $lang("clients.panels.address") }}</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-address-autocomplete
                    v-model="place"
                    type="address"
                    :label="$lang('main.address.search')"
                    solo
                    debounce="500"
                    prepend-inner-icon="mdi-magnify"
                    @input="$setAddress(place)"
                    append-icon="mdi-map-marker"
                  />
                  <c-countries-autocomplete
                    v-model="form.country"
                    :label="$lang('clients.fields.country')"
                  />
                  <v-text-field
                    v-model="form.address"
                    prepend-icon="mdi-map-marker"
                    :label="$lang('clients.fields.address')"
                  ></v-text-field>
                  <v-text-field
                    v-model="form.postcode"
                    prepend-icon="mdi-city"
                    :label="$lang('clients.fields.postcode')"
                  ></v-text-field>
                  <v-text-field
                    v-model="form.city"
                    prepend-icon="mdi-city"
                    :label="$lang('clients.fields.city')"
                  ></v-text-field>
                  <v-text-field
                    v-model="form.state"
                    prepend-icon="mdi-city"
                    :label="$lang('clients.fields.state')"
                    :error="!!validation.state"
                    :error-messages="!!validation.state ? validation.state : []"
                  ></v-text-field>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="mb-2">
              <v-card-title>{{ $lang("clients.helps.creation.title") }}</v-card-title>
              <v-card-text>{{ $lang("clients.helps.creation.text") }}</v-card-text>
            </v-card>
            <c-vat-code-check
              :country="form.country"
              :vat-code="form.vat_code"
              @updated="updateFormData"
            />
            <v-combobox
              v-model="form.tags"
              :items="tags"
              :search-input.sync="search"
              hide-selected
              :hint="$lang('clients.tags.maximum_tags')"
              :label="$lang('clients.fields.tags')"
              multiple
              persistent-hint
              small-chips
              :error="!!validation.tags"
              :error-messages="!!validation.tags ? validation.tags : []"
            >
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $lang('clients.tags.no_result') }} "
                      <strong>{{ search }}</strong>
                      ". {{ $lang('clients.tags.press_to_create') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-combobox>
            <c-languages-autocomplete
              v-model="form.locale"
              :label="$lang('clients.fields.locale')"
            />
            <v-textarea
              class="mt-2"
              v-model="form.notes"
              filled
              :label="$lang('clients.fields.notes')"
              hide-details
            ></v-textarea>
            <v-date
              v-model="form.birthday"
              :label="$lang('clients.fields.birthday')"
              clearable
            ></v-date>
            <v-boolean
              v-model="form.newsletter"
              :label="$lang('clients.fields.newsletter')"
            />
            <v-boolean
              v-model="form.is_blacklist"
              :label="$lang('clients.fields.is_blacklist')"
              color="black"
              append-icon="mdi-alert-decagram"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from "vuex";

import {
  CREATE_CLIENTS,
  CREATE_CLIENTS_RESET
  } from "@/store/clients/actions";

import form from "@/pages/clients/mixins/form";

export default {
  name: "CreationForm",
  mixins: [
    form
  ],
  props: {
    clientName: {
      type: String,
      required: false,
    },
    companyId: {
      type: Number,
      required: false
    }
  },
  created() {
    if (!!this.clientName) {
      this.form.name = this.clientName;
    }
    if (!!this.companyId) {
      this.form.company_id = this.companyId;
    }
  },
  methods: {
    resetAction() {
      return CREATE_CLIENTS_RESET;
    },
    submitAction() {
      return CREATE_CLIENTS;
    },
    submitActionOptions() {
      return this.form;
    }
  },
  computed: {
    ...mapState({
      loading: state => state.clients.creationRequest.loading,
      validation: state => state.clients.creationRequest.validation
    })
  },
};
</script>

<style scoped>
.v-input--is-disabled:not(.v-input--is-readonly) {
    pointer-events: auto;
}
</style>
